import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const SESSION_BANNER_DISMISS_KEY = 'pending_coupon_banner_dismissed';
const SESSION_CARD_DISMISS_KEY = 'pending_coupon_card_dismissed';

const SKIP_ROUTE_NAMES = new Set([
    'checkout',
    'register',
    'login',
    'recuperar-senha',
    'redefinir-senha',
    'google-callback',
    'confirm-email-boxed',
    'aceite-legal',
    'termos-de-servico',
    'politica-de-privacidade',
    'plano',
]);

function formatCurrency(value) {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return String(value ?? '');
    }

    return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function intervalSuffix(interval) {
    if (interval === 'YEARLY') {
        return '/ano';
    }
    if (interval === 'MONTHLY') {
        return '/mês';
    }
    if (interval === 'PER_DOCUMENT') {
        return ' na fatura mensal';
    }

    return '';
}

function buildDiscountBadge(coupon) {
    if (!coupon) {
        return '';
    }

    if (coupon.discount_type === 'percentage') {
        const percentage = parseFloat(coupon.discount_value);
        return Number.isNaN(percentage) ? 'OFF' : `${Math.round(percentage)}% OFF`;
    }

    return `R$ ${formatCurrency(coupon.discount_value)} OFF`;
}

function buildDiscountLabel(coupon) {
    if (!coupon) {
        return 'desconto';
    }

    if (coupon.discount_type === 'percentage') {
        const percentage = parseFloat(coupon.discount_value);
        return Number.isNaN(percentage) ? 'desconto' : `${Math.round(percentage)}% de desconto`;
    }

    return `R$ ${formatCurrency(coupon.discount_value)} de desconto`;
}

export function usePendingRegistrationCoupon(options = {}) {
    const surface = options.surface ?? 'banner';
    const store = useStore();
    const route = useRoute();
    const dismissed = ref(false);

    const user = computed(() => store.state.auth?.user);
    const pending = computed(() => user.value?.pending_registration_coupon ?? null);
    const isAvailable = computed(() => pending.value?.available === true);
    const isPlanScoped = computed(() => pending.value?.plan_scoped === true);

    const isBannerDismissedInSession = () => typeof sessionStorage !== 'undefined'
        && sessionStorage.getItem(SESSION_BANNER_DISMISS_KEY) === '1';

    const isCardDismissedInSession = () => typeof sessionStorage !== 'undefined'
        && sessionStorage.getItem(SESSION_CARD_DISMISS_KEY) === '1';

    const showPrompt = computed(() => {
        if (!isAvailable.value) {
            return false;
        }

        if (user.value?.pending_subscription) {
            return false;
        }

        if (user.value?.has_active_subscription) {
            return false;
        }

        if (user.value?.overdue_subscription) {
            return false;
        }

        if (SKIP_ROUTE_NAMES.has(route.name)) {
            return false;
        }

        if (surface === 'banner') {
            if (dismissed.value || isBannerDismissedInSession()) {
                return false;
            }

            return true;
        }

        if (surface === 'card') {
            if (!isBannerDismissedInSession()) {
                return false;
            }

            if (dismissed.value || isCardDismissedInSession()) {
                return false;
            }

            if (route.name !== 'Home') {
                return false;
            }

            return true;
        }

        return false;
    });

    const discountBadge = computed(() => buildDiscountBadge(pending.value));

    const checkoutRoute = computed(() => {
        const couponCode = pending.value?.coupon_code;

        if (!isPlanScoped.value) {
            return {
                name: 'plano',
                query: couponCode ? { cupom: couponCode } : {},
            };
        }

        if (!pending.value?.plan?.id) {
            return { name: 'plano' };
        }

        return {
            name: 'checkout',
            params: { id: pending.value.plan.id },
            query: couponCode ? { cupom: couponCode } : {},
        };
    });

    const headline = computed(() => {
        const coupon = pending.value;
        if (!coupon) {
            return '';
        }

        if (!isPlanScoped.value) {
            return coupon.description || `Você tem ${buildDiscountLabel(coupon)} nos planos mensais, anuais e por documento.`;
        }

        const planName = coupon.plan?.name || 'plano selecionado';
        const discountLabel = buildDiscountLabel(coupon);

        if (coupon.billing_mode === 'per_document_invoice' || coupon.plan?.interval === 'PER_DOCUMENT') {
            return `${discountLabel} na fatura mensal do ${planName}.`;
        }

        const price = formatCurrency(coupon.discounted_price);
        const suffix = intervalSuffix(coupon.plan?.interval);

        return `${discountLabel} no ${planName} — assine por R$ ${price}${suffix}.`;
    });

    const subline = computed(() => {
        const coupon = pending.value;
        if (!coupon) {
            return '';
        }

        if (!isPlanScoped.value) {
            if (coupon.affiliate?.name) {
                return `Cortesia de ${coupon.affiliate.name}. Escolha o plano e o desconto entra automaticamente no checkout.`;
            }

            return 'Escolha o plano ideal para você — o cupom do cadastro será aplicado no checkout.';
        }

        if (coupon.affiliate?.name) {
            return `Cortesia de ${coupon.affiliate.name}. Cupom aplicado automaticamente no checkout.`;
        }

        return 'Seu cupom do cadastro será aplicado automaticamente no checkout.';
    });

    const ctaLabel = computed(() => (isPlanScoped.value ? 'Assinar com desconto' : 'Escolher plano'));

    function dismissPrompt() {
        dismissed.value = true;

        if (typeof sessionStorage === 'undefined') {
            return;
        }

        const storageKey = surface === 'card'
            ? SESSION_CARD_DISMISS_KEY
            : SESSION_BANNER_DISMISS_KEY;

        sessionStorage.setItem(storageKey, '1');
    }

    return {
        pending,
        isAvailable,
        isPlanScoped,
        showPrompt,
        discountBadge,
        checkoutRoute,
        headline,
        subline,
        ctaLabel,
        dismissPrompt,
        formatCurrency,
    };
}
